import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  input,
  output,
} from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { ApprovalUpdate } from 'src/app/_models/requests';
import { ModalService } from 'src/app/_services/modal.service';
import { ButtonComponent } from '../../buttons/button/button.component';
import { UnderlinedTextareaFieldComponent } from '../../forms/underlined-textarea-field/underlined-textarea-field.component';
import { NotesParameterRanges } from 'src/app/_helpers/notes-min-max';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';

@Component({
    selector: 'app-provide-info-modal',
    templateUrl: './provide-info-modal.component.html',
    styleUrls: ['./provide-info-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        UnderlinedTextareaFieldComponent,
        ButtonComponent,
        MatIconButtonComponent
    ]
})
export class ProvideInfoModalComponent implements OnInit, OnDestroy {
  private readonly modalService = inject(ModalService);
  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly element: HTMLElement;
  isProcessing = false;
  minInfoLength = NotesParameterRanges.APPROVAL_INFO.minNoteLength;
  maxInfoLength = NotesParameterRanges.APPROVAL_INFO.maxNoteLength;

  infoField = new FormBase({
    key: 'infoText',
    label: 'Reason for request rejection',
    type: 'text',
    placeholder: 'Enter information here...',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  infoForm = this.fb.group({
    infoText: [
      { value: '', disabled: false },
      [Validators.required, Validators.maxLength(this.maxInfoLength)]
    ],
  });

  // Add a getter for easy form control access
  get f() {
    return this.infoForm.controls;
  }

  /** Modal id (required) */
  id = input<string>();

  /** Approval request object (required) */
  approvalRequest = input<ApprovalUpdate>();

  /**
   * Output event for clicked button on modal
   */
  // tslint:disable-next-line: no-output-on-prefix
  modalButtonClicked = output<string>();

  rejectionConfirmed = output<ApprovalUpdate>();

  constructor() {
    this.element = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const modal = this;

    // Close modal on background click - only if not processing a rejection
    if (!this.isProcessing) {
      this.element.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.className === 'modal--container') {
          modal.close();
        }
      });
    }
    // add self (this modal instance) to the modal service so it's accessible from controllers
    this.modalService.add(this);
    document.body.appendChild(this.element);
  }

  buttonEventHandler(): void {
    const ar = this.approvalRequest();
    if (ar) {
      ar.requestInfo = this.infoForm.value.infoText;
      this.rejectionConfirmed.emit(ar);
    }
    this.isProcessing = true;
  }

  // open modal
  open(): void {
    this.element.style.display = 'block';
  }

  // close modal
  close(): void {
    // Reset valdiation text value and close modal
    this.infoForm.reset();
    const ar = this.approvalRequest();
    if (ar) {
      ar.id = null;
      ar.requestInfo = '';
    }
    this.isProcessing = false;
    this.element.style.display = 'none';
  }

  // Remove self from modal service when component is destroyed
  ngOnDestroy(): void {
    this.modalService.remove(this.id());
    this.element.remove();
  }
}
